// assets/product-form.js
// Note: imports removed — fetchConfig, publish, PUB_SUB_EVENTS, and routes
// are already available as globals via constants.js, pubsub.js, and global.js
//
// NOTE: while the CART REDIRECT BLOCKER in layout/theme.liquid is present,
// onSubmitHandler below never runs for add-to-cart forms — the blocker owns
// the submit at document capture (it predates this file's customization and
// exists to beat shop-js's cart-sync listener). The badge update lives there.

if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        // — DOM hooks —
        this.form = this.querySelector('form');
        this.variantIdInput = this.form.querySelector('[name="id"]');
        this.variantIdInput.disabled = false;

        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.hideErrors = this.dataset.hideErrors === 'true';

        // — If using cart‐drawer, open it on add to cart —
        if (document.querySelector('cart-drawer')) {
          this.submitButton.setAttribute('aria-haspopup', 'dialog');
        }

        // — Form submit handler (capture: true fires BEFORE Shopify's shop-js listener) —
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this), true);
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        evt.stopImmediatePropagation();

        // — EMPLOYEE-ONLY ACCESS CHECK —
        const restrictedFlag = document.querySelector('#access-denied');
        if (restrictedFlag && restrictedFlag.dataset.restricted === 'true') {
          this.handleErrorMessage("⛔️ This product is only available to Blue Unicorn employees.");
          return;
        }
        // — end EMPLOYEE-ONLY ACCESS CHECK —

        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', 'true');
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner')?.classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);

        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }

        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMsg = this.submitButton.querySelector('.sold-out-message');
              if (soldOutMsg) {
                this.submitButton.setAttribute('aria-disabled', 'true');
                this.submitButtonText.classList.add('hidden');
                soldOutMsg.classList.remove('hidden');
                this.error = true;
              }
              return;
            }

            // — No cart drawer/notification present (Page cart mode).
            // Stay on the page and show brief confirmation instead of redirecting.
            if (!this.cart) {
              this.submitButtonText.textContent = '✓ Added to Cart';
              setTimeout(() => {
                this.submitButtonText.textContent = window.variantStrings?.addToCart || 'Add to cart';
              }, 2000);
              // Refresh the header cart icon by re-rendering its section. The
              // section response wraps the icon's INNER markup in
              // <div id="shopify-section-cart-icon-bubble"> — there is no
              // #cart-icon-bubble element inside it, so swap innerHTML into
              // the existing anchor rather than replacing a matching node.
              fetch(`${window.location.pathname}?section_id=cart-icon-bubble`)
                .then((r) => (r.ok ? r.text() : Promise.reject()))
                .then((html) => {
                  const fresh = new DOMParser()
                    .parseFromString(html, 'text/html')
                    .querySelector('.shopify-section');
                  const current = document.getElementById('cart-icon-bubble');
                  if (fresh && current) current.innerHTML = fresh.innerHTML;
                })
                .catch(() => {});
              return;
            }

            if (!this.error) {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              });
            }
            this.error = false;

            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => this.cart.renderContents(response));
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              this.cart.renderContents(response);
            }
          })
          .catch((error) => {
            console.error(error);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart?.classList.contains('is-empty')) {
              this.cart.classList.remove('is-empty');
            }
            if (!this.error) {
              this.submitButton.removeAttribute('aria-disabled');
            }
            this.querySelector('.loading__spinner')?.classList.add('hidden');
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper ||
          this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;

        this.errorMessage =
          this.errorMessage ||
          this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);
        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name="id"]');
      }
    }
  );
}